import { Radio } from "lucide-react";
import { useLive } from "@/contexts/LiveContext";

/**
 * LiveBanner — bright, pulsing red ribbon shown directly under the
 * AlertBar whenever someone in any of the user's groups is currently
 * Live. Tapping "Join In" opens the LiveRoomModal for that session.
 *
 * If there are multiple concurrent sessions we surface the most recent
 * one in the banner and surface the rest via the +N pill so the bar
 * never grows tall enough to push the calendar off the fold.
 */
export default function LiveBanner() {
  const { sessions, openRoom } = useLive();
  if (!sessions || sessions.length === 0) return null;

  const primary = sessions[0];
  const extra = sessions.length - 1;

  return (
    <div
      className="w-full px-3 sm:px-5 py-2 flex items-center gap-3"
      style={{
        background:
          "linear-gradient(90deg, rgba(255,59,48,0.95), rgba(255,45,146,0.9))",
        color: "#fff",
        borderBottom: "1px solid rgba(0,0,0,0.15)",
        animation: "bd-live-pulse 1.6s ease-in-out infinite",
      }}
      data-testid="live-banner"
    >
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bd-mono"
        style={{
          background: "rgba(0,0,0,0.25)",
          fontSize: "0.65rem",
          letterSpacing: "0.08em",
          fontWeight: 800,
        }}
        aria-label="Live indicator"
      >
        <Radio size={11} /> LIVE
      </span>
      <div className="flex-1 min-w-0 text-sm">
        <span className="font-bold truncate" data-testid="live-banner-title">
          {primary.host_name || "Someone"} is live now
        </span>
        <span className="hidden sm:inline ml-2 opacity-90 truncate">
          · {primary.title || "Tap Join In to participate"}
        </span>
      </div>
      {extra > 0 && (
        <button
          type="button"
          onClick={() => openRoom(sessions[1]?.session_id)}
          className="text-xs font-semibold px-2 py-0.5 rounded-full"
          style={{
            background: "rgba(255,255,255,0.18)",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.3)",
            cursor: "pointer",
          }}
          data-testid="live-banner-extra"
          title={`${extra} other live session${extra > 1 ? "s" : ""}`}
        >
          +{extra} more
        </button>
      )}
      <button
        type="button"
        onClick={() => openRoom(primary.session_id)}
        className="font-bold uppercase"
        style={{
          background: "#fff",
          color: "#ff3b30",
          border: "none",
          borderRadius: "999px",
          padding: "0.35rem 0.9rem",
          fontSize: "0.78rem",
          letterSpacing: "0.04em",
          cursor: "pointer",
          boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
        }}
        data-testid="live-banner-join"
      >
        Join In
      </button>
      <style>{`
        @keyframes bd-live-pulse {
          0%, 100% { box-shadow: inset 0 0 0 0 rgba(255,255,255,0); }
          50% { box-shadow: inset 0 0 24px 0 rgba(255,255,255,0.25); }
        }
      `}</style>
    </div>
  );
}
